import { DOCUMENT } from '@angular/common';
import {
  ContentChild,
  DestroyRef,
  Directive,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { fromEvent, merge, take, takeUntil } from 'rxjs';
import { map } from 'rxjs/operators';

import { DrawerDragHandleComponent } from '../components/drawer-drag-handle/drawer-drag-handle.component';
import { DrawerWidth } from '../types';

@Directive({
  standalone: true,
  selector: '[rtDrawerResize]',
})
export class DrawerResizeDirective {
  @Input({ alias: 'rtDrawerResizeBoundaries' }) public boundaries: [number, number] = [
    DrawerWidth.Min,
    DrawerWidth.Max,
  ];

  @Output() public drawerSizeChanges: EventEmitter<number> = new EventEmitter<number>();

  @ContentChild(DrawerDragHandleComponent) public dragHandle: DrawerDragHandleComponent;

  constructor(
    private elRef: ElementRef,
    @Inject(DOCUMENT) public document: Document,
    private destroyRef: DestroyRef,
  ) {}

  public ngAfterContentInit() {
    this.watchMouseDownEvents();
  }

  private startResize(event: MouseEvent) {
    event.preventDefault();

    const startWidth = this.elRef.nativeElement.offsetWidth;
    const startX = event.clientX;

    const mouseMove$ = fromEvent<MouseEvent>(this.document, 'mousemove');
    const mouseUp$ = fromEvent<MouseEvent>(this.document, 'mouseup');

    const resize$ = mouseMove$.pipe(
      map((e: MouseEvent) => startWidth - (e.clientX - startX)),
      takeUntil(mouseUp$),
    );

    const subscription = resize$.subscribe((newWidth: number) => {
      const [min, max] = this.boundaries;
      if (newWidth > min && newWidth < max) {
        this.elRef.nativeElement.style.width = `${newWidth}px`;
      }
    });

    const stopResize = () => {
      subscription.unsubscribe();
    };

    merge(mouseUp$, fromEvent(this.document, 'mouseleave')).pipe(take(1)).subscribe(stopResize);
  }

  private watchMouseDownEvents(): void {
    fromEvent(this.dragHandle.elementRef.nativeElement, 'mousedown')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event: MouseEvent) => this.startResize(event));
  }
}
